import {ChangeEvent, FC, useContext, useEffect} from 'react';
import {useCreateApiary} from '../lib/usCreateApiary';
import {
  CreateApiaryMapModalBody,
  CreateApiaryMapModalFooter,
  CreateApiaryMapModalMarker,
  CreateApiaryModalBody,
  CreateApiaryModalFooter,
  CreateApiaryModalHead,
  CreateApiaryModalTitle,
  CreateApiaryModalWrapper,
} from './styles';
import Button from '@atomaro/ui-kit/components/Button/Button/Button';
import {ChevronRightIcon, CloseIcon} from 'shared/ui/icons';
import {toRem} from 'shared/helpers';
import {Controller} from 'react-hook-form';
import Input from '@atomaro/ui-kit/components/Input/Input';
import Modal from '@atomaro/ui-kit/components/Modal/Modal';
import {COLORS} from 'shared/consts';
import {useApiariesStore} from 'entities/apiaries';
import {useReactify} from 'shared/api';
import {ConfigContext} from '../../../app/App.tsx';

type CreateApiaryProps = {
  isOpen: boolean;
  isMobile?: boolean;
  onClose: () => void;
  position: 'top' | 'bottom' | 'left' | 'right' | 'submenu';
  edit?: boolean;
};
export const CreateApiary: FC<CreateApiaryProps> = ({isOpen, isMobile, onClose, edit}) => {
  const currentApiary = useApiariesStore((s) => s.currentApiary());
  const {
    control,
    errors,
    coordinates,
    isMapModalOpen,
    location,
    mapInstance,
    formValues,
    userCoords,
    handleSubmit,
    onSubmit,
    onSubmitEdit,
    reset,
    setValue,
    onNameChange,
    onNameReset,
    onCoordsFieldFocus,
    onMapModalClose,
    onMapModalSave,
    onMapClick,
    onCoordinatesChange,
  } = useCreateApiary(onClose, edit, currentApiary);
  const {VITE_YANDEX_TOKEN} = useContext(ConfigContext);

  const onReset = () => {
    onClose();
    reset();
  };

  const toNumCoords = (c: any): [number, number] | null => {
    if (!Array.isArray(c) || c.length !== 2) return null;
    const a = Number(c[0]);
    const b = Number(c[1]);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    return [a, b];
  };

  const switchCoordinates = (event: ChangeEvent<HTMLInputElement>) => {
    const ddRegex = /^-?\d+(\.\d+)?,\s-?\d+(\.\d+)?$/;
    const isValidCoords = (coords: string) => {
      return ddRegex.test(coords);
    };
    const newEvent = event;
    if (isValidCoords(event.target.value)) {
      const coordsArray = event.target.value.split(', ');
      newEvent.target.value = [coordsArray[1], coordsArray[0]].join(', ');
    }
    return newEvent;
  };

  useEffect(() => {
    if (isOpen && edit && currentApiary) {
      setValue('name', currentApiary.name);
      setValue(
        'coordinates',
        `${currentApiary.coordinates.longitude}, ${currentApiary.coordinates.latitude}`
      );
    }
  }, [isOpen, edit, currentApiary, setValue]);

  const reactify = useReactify(VITE_YANDEX_TOKEN);

  if (!reactify) return null;

  const safeCenter = toNumCoords(coordinates) ?? toNumCoords(userCoords) ?? [37.613997, 55.756736];
  const safeZoom = Number.isFinite((location as any)?.zoom) ? (location as any).zoom : 12;
  const safeLocation = {zoom: safeZoom, center: safeCenter};

  return (
    <>
      <Modal
        isOpen={isOpen}
        centered
        style={{padding: 0, maxHeight: '100%'}}
        onClickOverlay={onClose}
      >
        <CreateApiaryModalWrapper onSubmit={handleSubmit(edit ? onSubmitEdit : onSubmit)}>
          <CreateApiaryModalHead>
            {edit ? (
              <CreateApiaryModalTitle>Изменить пасеку</CreateApiaryModalTitle>
            ) : (
              <CreateApiaryModalTitle>Создать пасеку</CreateApiaryModalTitle>
            )}
            <Button
              iconPrefix={<CloseIcon />}
              variant="ghost"
              colorScheme="neutral"
              style={{width: toRem(24)}}
              size={isMobile ? 's' : 'm'}
              onClick={onClose}
            />
          </CreateApiaryModalHead>
          <CreateApiaryModalBody>
            <Controller
              control={control}
              name="name"
              rules={{required: 'Введите название пасеки'}}
              render={({field}) => (
                <Input
                  size="l"
                  label="Название"
                  iconSuffix={Boolean(field.value?.trim?.()) && <CloseIcon />}
                  name={field.name}
                  value={field.value}
                  onChange={(event) => onNameChange(event, field)}
                  onClickIconSuffix={onNameReset}
                  error={errors.name ? errors.name.message : undefined}
                />
              )}
            />
            <Controller
              control={control}
              name="coordinates"
              rules={{required: 'Выберите координаты'}}
              render={({field}) => (
                <Input
                  size="l"
                  label="Координаты"
                  iconSuffix={<ChevronRightIcon />}
                  name={field.name}
                  value={field.value}
                  error={errors.coordinates ? errors.coordinates.message : undefined}
                  onChange={(event) => onCoordinatesChange(switchCoordinates(event), field)}
                />
              )}
            />
            <reactify.YMap location={safeLocation} className="create-apiary-minimap" behaviors={[]}>
              <reactify.YMapDefaultSchemeLayer />
              <reactify.YMapDefaultFeaturesLayer />
              {coordinates && (
                <reactify.YMapMarker coordinates={coordinates}>
                  <CreateApiaryMapModalMarker>
                    <svg
                      width="35"
                      height="40"
                      viewBox="0 0 35 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M19.505 0.548624C18.2646 -0.182835 16.7354 -0.182878 15.495 0.548515L2.0056 8.49989C0.764582 9.23138 0 10.5833 0 12.0464V27.9524C0 29.4154 0.764587 30.7672 2.005 31.4987L15.495 39.4514C16.7354 40.1829 18.2646 40.1829 19.505 39.4514L32.995 31.4987C34.2354 30.7672 35 29.4154 35 27.9524V12.0475C35 10.5845 34.2354 9.23261 32.995 8.50112L19.505 0.548624Z"
                        fill={COLORS.ACCENT.DEFAULT}
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M17.6929 10.9598C17.8829 10.8812 18.0999 10.8941 18.2809 10.9988L22.4316 13.4012C22.6412 13.5225 22.7706 13.7487 22.7707 13.9933V18.4029L26.5822 20.6091C26.7919 20.7305 26.9213 20.9566 26.9214 21.2012V26.0068C26.9213 26.2514 26.7918 26.4776 26.5822 26.599L22.4316 29.0013C22.2247 29.121 21.9706 29.121 21.7637 29.0013L17.947 26.7916L14.1303 29.0013C13.9234 29.121 13.6693 29.121 13.4625 29.0013L9.31182 26.599C9.1022 26.4776 8.97276 26.2514 8.97266 26.0068V21.2012L8.97879 21.1107C9.00643 20.9019 9.12833 20.7153 9.31182 20.6091L13.1233 18.4029V13.9933C13.1234 13.7487 13.2528 13.5225 13.4625 13.4012L17.6131 10.9988L17.6929 10.9598ZM10.3188 21.5963V25.6109L13.7964 27.6235L17.2739 25.6109V21.5963L13.7964 19.5837L10.3188 21.5963ZM18.6201 21.5963V25.6109L22.0977 27.6235L25.5752 25.6109V21.5963L22.0977 19.5837L18.6201 21.5963ZM14.4695 14.3884V18.4029L17.947 20.4155L21.4246 18.4029V14.3884L17.947 12.3758L14.4695 14.3884Z"
                        fill={COLORS.STATIC.WHITE}
                      />
                    </svg>
                  </CreateApiaryMapModalMarker>
                </reactify.YMapMarker>
              )}

              <reactify.YMapListener onClick={onCoordsFieldFocus} />
            </reactify.YMap>
          </CreateApiaryModalBody>
          <CreateApiaryModalFooter>
            <Button
              label="Отмена"
              variant="secondary"
              colorScheme="neutral"
              onClick={onReset}
              size="l"
            />
            <Button
              label={edit ? 'Изменить пасеку' : 'Создать пасеку'}
              type="submit"
              size="l"
              disabled={!formValues.name || !formValues.coordinates}
            />
          </CreateApiaryModalFooter>
        </CreateApiaryModalWrapper>
      </Modal>

      <Modal
        isOpen={isMapModalOpen}
        centered
        style={{padding: 0, maxHeight: '100%'}}
        onClickOverlay={onMapModalClose}
      >
        <CreateApiaryModalWrapper $minWidth={640}>
          <CreateApiaryModalHead>
            <CreateApiaryModalTitle>Координаты пасеки</CreateApiaryModalTitle>
            <Button
              iconPrefix={<CloseIcon />}
              variant="ghost"
              colorScheme="neutral"
              style={{width: toRem(24)}}
              size={isMobile ? 's' : 'm'}
              onClick={onMapModalClose}
            />
          </CreateApiaryModalHead>
          <CreateApiaryMapModalBody>
            <reactify.YMap
              location={{zoom: safeZoom, center: safeCenter}}
              className="create-apiary-map"
              ref={mapInstance}
            >
              <reactify.YMapDefaultSchemeLayer />
              <reactify.YMapDefaultFeaturesLayer />

              <reactify.YMapListener onClick={onMapClick} />

              {coordinates && (
                <reactify.YMapMarker coordinates={coordinates}>
                  <CreateApiaryMapModalMarker>
                    <svg
                      width="35"
                      height="40"
                      viewBox="0 0 35 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M19.505 0.548624C18.2646 -0.182835 16.7354 -0.182878 15.495 0.548515L2.0056 8.49989C0.764582 9.23138 0 10.5833 0 12.0464V27.9524C0 29.4154 0.764587 30.7672 2.005 31.4987L15.495 39.4514C16.7354 40.1829 18.2646 40.1829 19.505 39.4514L32.995 31.4987C34.2354 30.7672 35 29.4154 35 27.9524V12.0475C35 10.5845 34.2354 9.23261 32.995 8.50112L19.505 0.548624Z"
                        fill={COLORS.ACCENT.DEFAULT}
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M17.6929 10.9598C17.8829 10.8812 18.0999 10.8941 18.2809 10.9988L22.4316 13.4012C22.6412 13.5225 22.7706 13.7487 22.7707 13.9933V18.4029L26.5822 20.6091C26.7919 20.7305 26.9213 20.9566 26.9214 21.2012V26.0068C26.9213 26.2514 26.7918 26.4776 26.5822 26.599L22.4316 29.0013C22.2247 29.121 21.9706 29.121 21.7637 29.0013L17.947 26.7916L14.1303 29.0013C13.9234 29.121 13.6693 29.121 13.4625 29.0013L9.31182 26.599C9.1022 26.4776 8.97276 26.2514 8.97266 26.0068V21.2012L8.97879 21.1107C9.00643 20.9019 9.12833 20.7153 9.31182 20.6091L13.1233 18.4029V13.9933C13.1234 13.7487 13.2528 13.5225 13.4625 13.4012L17.6131 10.9988L17.6929 10.9598ZM10.3188 21.5963V25.6109L13.7964 27.6235L17.2739 25.6109V21.5963L13.7964 19.5837L10.3188 21.5963ZM18.6201 21.5963V25.6109L22.0977 27.6235L25.5752 25.6109V21.5963L22.0977 19.5837L18.6201 21.5963ZM14.4695 14.3884V18.4029L17.947 20.4155L21.4246 18.4029V14.3884L17.947 12.3758L14.4695 14.3884Z"
                        fill={COLORS.STATIC.WHITE}
                      />
                    </svg>
                  </CreateApiaryMapModalMarker>
                </reactify.YMapMarker>
              )}

              <reactify.YMapControls position="right" orientation="vertical">
                <reactify.YMapZoomControl />
                <reactify.YMapGeolocationControl />
              </reactify.YMapControls>
            </reactify.YMap>
          </CreateApiaryMapModalBody>
          {Boolean(coordinates) && (
            <CreateApiaryMapModalFooter>
              <b>Точка на карте</b>
              <p>
                <span>Координаты</span>
                <span>{coordinates?.join(', ')}</span>
              </p>
              <Button label="Сохранить" onClick={onMapModalSave} size="l" />
            </CreateApiaryMapModalFooter>
          )}
        </CreateApiaryModalWrapper>
      </Modal>

      {/* <Drawer isOpen={false} position={position} onClose={onClose}>
        <NewEventWrapper onSubmit={handleSubmit(onSubmit)}>
          <NewEventHead $isMobile={isMobile}>
            <Button 
              iconPrefix={<CloseIcon />}
              variant={isMobile ? 'ghost' : "secondary"}
              colorScheme="neutral"
              style={{alignSelf: 'flex-end', width: isMobile ? toRem(24) : toRem(36)}}
              size={isMobile ? 's' : 'm'}
              onClick={onClose}
            />
            <NewEventTitle>
              Новая пасека
            </NewEventTitle>
            {isMobile && <Spacer width={toRem(24)} height={toRem(24)} />}
          </NewEventHead>
          <NewEventBody>
            <NewEventRow>
              <span>Название</span>
              <Controller 
                control={control}
                name="name"
                rules={{required: 'Поле обязательно'}}
                render={({field}) => (
                  <Input 
                    size="m"
                    error={errors.name?.message}
                    placeholder="Название"
                    name={field.name}
                    value={field.value}
                    onChange={(event) => onNameChange(event, field)}
                  />
                )}
              />
            </NewEventRow>
            <NewEventRow>
              <span>Широта</span>
              <Controller 
                control={control}
                name="latitude"
                rules={{required: 'Поле обязательно', pattern: {value: /-?\d{1,3}\.\d+/gm, message: 'Значение не соответствует реальным данным'}}}
                render={({field}) => (
                  <Input 
                    size="m"
                    error={errors.latitude?.message}
                    placeholder="Широта"
                    name={field.name}
                    value={`${field.value}`}
                    onChange={(event) => onLatChange(event, field)}
                  />
                )}
              />
            </NewEventRow>
            <NewEventRow>
              <span>Долгота</span>
              <Controller 
                control={control}
                name="longitude"
                rules={{required: 'Поле обязательно', pattern: {value: /-?\d{1,3}\.\d+/gm, message: 'Значение не соответствует реальным данным'}}}
                render={({field}) => (
                  <Input 
                    size="m"
                    error={errors.longitude?.message}
                    placeholder="Долгота"
                    name={field.name}
                    value={`${field.value}`}
                    onChange={(event) => onLongChange(event, field)}
                  />
                )}
              />
            </NewEventRow>
          </NewEventBody>
          <NewEventFooter>
            <Button 
              type='submit'
              label="Сохранить"
            />
            <Button 
              label="Отменить"
              variant='ghost'
              colorScheme='neutral'
              onClick={onReset}
            />
          </NewEventFooter>
        </NewEventWrapper>
      </Drawer> */}
    </>
  );
};
